import { rem } from '../tokens/spacing';
import type { BrandConfig } from './types';

export type HeadingScale = 'default' | 'compact' | 'editorial';

interface HeadingDef {
  size: number;
  weight: number;
  lineHeight: number;
}

const SCALES: Record<HeadingScale, Record<string, HeadingDef>> = {
  default: {
    h1: { size: 32, weight: 700, lineHeight: 1.2 },
    h2: { size: 24, weight: 700, lineHeight: 1.2 },
    h3: { size: 18, weight: 600, lineHeight: 1.2 },
    h4: { size: 16, weight: 600, lineHeight: 1.5 },
    h5: { size: 16, weight: 600, lineHeight: 1.5 },
    h6: { size: 14, weight: 600, lineHeight: 1.5 },
    body: { size: 16, weight: 400, lineHeight: 1.5 },
    label: { size: 12, weight: 500, lineHeight: 1.5 },
    caption: { size: 10, weight: 400, lineHeight: 1.5 },
  },
  compact: {
    h1: { size: 26, weight: 700, lineHeight: 1.2 },
    h2: { size: 20, weight: 700, lineHeight: 1.2 },
    h3: { size: 16, weight: 600, lineHeight: 1.2 },
    h4: { size: 14, weight: 600, lineHeight: 1.4 },
    h5: { size: 14, weight: 600, lineHeight: 1.4 },
    h6: { size: 12, weight: 600, lineHeight: 1.4 },
    body: { size: 14, weight: 400, lineHeight: 1.5 },
    label: { size: 11, weight: 500, lineHeight: 1.5 },
    caption: { size: 10, weight: 400, lineHeight: 1.5 },
  },
  editorial: {
    h1: { size: 40, weight: 700, lineHeight: 1.15 },
    h2: { size: 32, weight: 700, lineHeight: 1.2 },
    h3: { size: 24, weight: 600, lineHeight: 1.25 },
    h4: { size: 20, weight: 600, lineHeight: 1.4 },
    h5: { size: 18, weight: 600, lineHeight: 1.4 },
    h6: { size: 16, weight: 600, lineHeight: 1.5 },
    body: { size: 18, weight: 400, lineHeight: 1.6 },
    label: { size: 13, weight: 500, lineHeight: 1.5 },
    caption: { size: 11, weight: 400, lineHeight: 1.5 },
  },
};

/**
 * Emits `--ui-heading-{variant}-size`, `--ui-heading-{variant}-weight`,
 * and `--ui-heading-{variant}-lh` CSS variables based on the brand's heading scale.
 */
export function getTypographyTokens(brand: BrandConfig): Record<string, string> {
  const scale = brand.headingScale ?? 'default';
  const defs = SCALES[scale];
  const tokens: Record<string, string> = {};

  for (const [variant, def] of Object.entries(defs)) {
    tokens[`--ui-heading-${variant}-size`] = rem(def.size);
    tokens[`--ui-heading-${variant}-weight`] = String(def.weight);
    tokens[`--ui-heading-${variant}-lh`] = String(def.lineHeight);
  }

  return tokens;
}
